import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants, ImportTemplates } from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import './ReviewStatusComponent.scss';
import { Toast } from 'primereact/toast';
import DataManagementForm from '../DataManagement/DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';
import DataManagementDataTable from '../DataManagement/DataManagementDataTable';
import { Column } from 'primereact/column';
import { createDataManagementQuery, useDataManagementPagination } from '../DataManagement/useDataManagementPagination';

const renderReviewStatusActions = (status: any, onEdit: (status: any) => void, onDelete: (status: any) => void) => (
  <>
    <Button icon="pi pi-pencil" className="p-button-text" style={{ marginRight: 8 }} onClick={() => onEdit(status)} />
    <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => onDelete(status)} />
  </>
);

interface ReviewStatusComponentProps {
  providerTypes: { label: string; value: string }[];
}

const ReviewStatusComponent: React.FC<ReviewStatusComponentProps> = ({ providerTypes }) => {
  // Inject custom style for dialog overlay to ensure lighter background
  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles('review-status-styles');
    return cleanup;
  }, []);

  const toast = React.useRef<Toast>(null);
  const { uploadFile } = useDataManagementFileUpload();

  const handleExcelUpload = async (file: File) => {
    setLoading(true);
    try {
      const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.REVIEW_STATUSES);

      await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
        fileKey,
        fileName: file.name,
        module: Constants.DATA_MANAGEMENT_MODULES.REVIEW_STATUSES,
      });

      await refresh();
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Review Statuses uploaded successfully', life: 3000 });
    } catch (err: any) {
      let msg = err?.response?.data?.message || err?.message || "Bulk upload failed";
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.reviewStatuses,
    onUpload: handleExcelUpload,
    title: 'Import Review Statuses from Excel',
    prominentDownloadLink: true,
  });

  const [providerTypeFilter, setProviderTypeFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState<any>(null);

  const query = createDataManagementQuery({ providerType: providerTypeFilter, search: searchTerm });
  const {
    items: paginatedStatuses,
    totalRecords,
    page,
    setPage,
    loading,
    setLoading,
    error,
    refresh,
  } = useDataManagementPagination<any>(ApiEndpoints.GetPagedReviewStatuses, query, {
    debounceMs: searchTerm ? 300 : 0,
    errorPrefix: 'Failed to fetch review statuses',
    preserveItemsOnError: true,
    recoverEmptyPageWhenNoResults: true,
  });

  // Get unique provider types for dropdown (filter)
  const providerTypeOptions = providerTypes && providerTypes.length > 0
    ? providerTypes
    : Array.from(new Set(paginatedStatuses.map(s => s.providerType).filter(Boolean))).map(pt => ({ label: pt, value: pt }));

  const editStatus = (status: any) => { setSelectedStatus({ ...status }); setIsAddMode(false); setEditDialogVisible(true); };
  const deleteStatus = (status: any) => { setStatusToDelete(status); setDeleteDialogVisible(true); };

  return (
    <div style={{ marginTop: 24, position: 'relative', minHeight: 220 }} aria-busy={loading}>
      <DataManagementLoader visible={loading} label="Loading review statuses" />
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New Review Status" icon="pi pi-plus" onClick={() => {
            setSelectedStatus({ statusId: 0, statusName: '', statusCode: '', sequence: 0, providerType: '' });
            setIsAddMode(true);
            setEditDialogVisible(true);
          }} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} style={{ marginLeft: 8 }} />
        </div>
        <div className="review-status-search-anim">
          <input
            type="text"
            placeholder="Search by Status Name..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
          />
          <span className="pi pi-search" />
        </div>
        <div className="provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={e => { setProviderTypeFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Provider Types</option>
            {providerTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>
      <DataManagementDataTable value={paginatedStatuses} dataKey="statusId" page={page} totalRecords={totalRecords}
        onPage={setPage} loading={loading} emptyMessage="No review statuses found.">
        <Column header="#" body={(_status: any, options) => (page - 1) * 10 + options.rowIndex + 1} headerStyle={{ width: 64, minWidth: 40 }} />
        <Column field="statusName" header="Status Name" body={(status: any) => status.statusName || '-'} />
        <Column field="statusCode" header="Status Code" body={(status: any) => status.statusCode || '-'} />
        <Column field="sequence" header="Sequence" body={(status: any) => status.sequence ?? '-'} />
        <Column field="providerType" header="Provider Type" body={(status: any) => status.providerType || '-'} />
        <Column header="Actions" body={(status: any) => renderReviewStatusActions(status, editStatus, deleteStatus)} />
      </DataManagementDataTable>
      <Dialog
        header={isAddMode ? "Add Review Status" : `Edit Review Status${selectedStatus && selectedStatus.statusName ? ` - ${selectedStatus.statusName}` : ''}`}
        visible={editDialogVisible}
        style={{ width: '500px', maxWidth: '90vw' }}
        onHide={() => { setEditDialogVisible(false); setIsAddMode(false); }}
        footer={
          <div>
            <Button label="Cancel" onClick={() => { setEditDialogVisible(false); setIsAddMode(false); }} className="p-button-text" />
            <Button label="Save" onClick={async () => {
              const validationErrors = getFormValidationErrors(dataManagementFormConfig.reviewStatus, selectedStatus);
              if (validationErrors.length > 0) {
                toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
                return;
              }
              const payload = normalizeFieldValues(dataManagementFormConfig.reviewStatus, selectedStatus);
              if (isAddMode) {
                try {
                  setLoading(true);
                  await httpService.post(ApiEndpoints.AddReviewStatus, payload);
                  await refresh();
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Review Status added successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              } else if (selectedStatus) {
                try {
                  setLoading(true);
                  await httpService.put(`${ApiEndpoints.UpdateReviewStatus}`, { ...payload, isActive: selectedStatus.isActive });
                  await refresh();
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Review Status updated successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              }
            }} loading={loading} disabled={loading} autoFocus />
          </div>
        }
      >
        <ReviewStatusEditForm
          editStatus={selectedStatus}
          setEditStatus={setSelectedStatus}
          providerTypes={providerTypes}
          isEditing={!isAddMode}
        />
      </Dialog>
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Yes" autoFocus={false} onClick={async () => {
              if (!statusToDelete) return;
              try {
                setLoading(true);
                await httpService.delete(`${ApiEndpoints.DeleteReviewStatus}/${statusToDelete.statusId}`);
                await refresh();
                setDeleteDialogVisible(false);
                setStatusToDelete(null);
              } catch (err: any) {
                const msg = err?.response?.data?.message || err?.message || 'Failed to delete review status';
                toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
              } finally {
                setLoading(false);
              }
            }} className="p-button-danger" />
            <Button label="No" autoFocus={true} onClick={() => setDeleteDialogVisible(false)} className="p-button-secondary" />
          </div>
        }
      >
        <div style={{ fontSize: 16, margin: '16px 0' }}>
          Are you sure you want to delete this record{statusToDelete && statusToDelete.statusName ? `: ${statusToDelete.statusName}` : ''}?
        </div>
      </Dialog>
    </div>
  );
}

interface ReviewStatusEditFormProps {
  editStatus: any;
  setEditStatus: (status: any) => void;
  providerTypes: { label: string; value: string }[];
  isEditing: boolean;
}

const ReviewStatusEditForm: React.FC<ReviewStatusEditFormProps> = ({ editStatus, setEditStatus, providerTypes, isEditing }) => {
  return (
    <DataManagementForm
      fields={dataManagementFormConfig.reviewStatus}
      values={editStatus}
      onChange={setEditStatus}
      options={{ providerType: providerTypes }}
      isEditing={isEditing}
    />
  );
};

export default ReviewStatusComponent;